import { t } from '@lingui/core/macro';
import type { CarRecordListForBooking, TireLeadReason, TireSeason } from '@navio/nsdk';
import { NoddiCard, NoddiInfoField } from 'noddi-ui-web';
import { getCarDisplayName, MISSING_VALUE } from 'noddi-util';
import {
  getAxleLabels,
  getReasonOptions,
  getSeasonOptions,
  getStudsOptions,
  getTiresLabel,
  type PassengerCarWheelPosition,
  type TireSelection
} from './requestOfferOptions';
import { getRequestSizes, type TireAxle, type TireSizesToSend } from './tireSizes';

type Props = {
  cars: CarRecordListForBooking[];
  /** One per car and season, as each is sent. */
  requests: { car: CarRecordListForBooking; season: TireSeason; sizesToSend: TireSizesToSend | null }[];
  tireSelection: TireSelection;
  positions: readonly PassengerCarWheelPosition[];
  reasons: TireLeadReason[];
  /** The studs answer for winter tires; null when no winter tires are asked for. */
  isStudded: boolean | null;
};

/** Everything a request sends, read back before submitting. */
export const RequestSummary = ({ cars, requests, tireSelection, positions, reasons, isStudded }: Props) => {
  const seasonLabels = getSeasonOptions();
  const reasonLabels = getReasonOptions()
    .filter(({ value }) => reasons.includes(value))
    .map(({ label }) => label)
    .join(', ');
  return (
    <div className='flex flex-col gap-2'>
      <p className='font-semibold'>{t`Your request`}</p>
      <NoddiCard contentClassName='flex flex-col gap-2'>
        <NoddiInfoField label={t`Tires`} value={getTiresLabel(tireSelection, positions)} />
        <NoddiInfoField label={t`Reason`} value={reasonLabels} />
        {isStudded !== null && (
          <NoddiInfoField
            label={t`Winter tires`}
            value={getStudsOptions().find(({ value }) => value === isStudded)?.label}
          />
        )}
      </NoddiCard>
      {cars.map((car) => (
        <NoddiCard key={car.id} contentClassName='flex flex-col gap-2'>
          {/* Not the card title: that is sentence-cased, which lowercases the make, model and plate. */}
          <p className='font-semibold'>{getCarDisplayName({ car, showYear: false })}</p>
          {requests
            .filter((request) => request.car.id === car.id)
            .map(({ season, sizesToSend }) => (
              <NoddiInfoField
                key={season}
                label={seasonLabels.find(({ value }) => value === season)?.label ?? season}
                value={<SizeValue sizes={getRequestSizes(car, season, sizesToSend)} />}
              />
            ))}
        </NoddiCard>
      ))}
    </div>
  );
};

const SizeValue = ({ sizes }: { sizes: { axle: TireAxle; size: string | null }[] }) => {
  const distinct = [...new Set(sizes.map(({ size }) => size))];
  if (distinct.length === 1) {
    return distinct[0] ?? MISSING_VALUE;
  }
  const axleLabels = getAxleLabels();
  return sizes.map(({ axle, size }) => `${axleLabels[axle]}: ${size ?? MISSING_VALUE}`).join(' · ');
};
